import { useMemo, useState } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import { ArrowLeft, CalendarCheck } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import type { AppLayoutContext } from '~/types';
import { useSchoolAttendanceReport } from './useSchoolAttendanceReport';

function isoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

export default function AttendanceOverview() {
  const { schoolSlug, yearSlug } = useParams();
  const { school } = useOutletContext<AppLayoutContext>();
  const schoolYear = school?.schoolYears?.find((year) => year.slug === yearSlug);

  const today = useMemo(() => isoDate(new Date()), []);
  const twoWeeksAgo = useMemo(() => isoDate(new Date(Date.now() - (13 * 24 * 60 * 60 * 1000))), []);
  const [fromDate, setFromDate] = useState(twoWeeksAgo);
  const [toDate, setToDate] = useState(today);

  const { data, loading, error } = useSchoolAttendanceReport(schoolYear?.id, fromDate, toDate);
  const report = data?.schoolYear?.schoolAttendanceReport;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon" aria-label="Back to schedule">
          <Link to={`/app/${schoolSlug}/years/${yearSlug}/schedule`}><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Attendance report</h1>
          <p className="text-muted-foreground">Every attendance-enabled block, school-wide. Blank days are not treated as absences.</p>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <div className="space-y-2"><Label htmlFor="report-from">From</Label><Input id="report-from" type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} /></div>
          <div className="space-y-2"><Label htmlFor="report-to">To</Label><Input id="report-to" type="date" value={toDate} min={fromDate} onChange={(event) => setToDate(event.target.value)} /></div>
          {report && <p className="text-sm text-muted-foreground">{report.recordedDayCount} recorded day{report.recordedDayCount === 1 ? '' : 's'} in this range.</p>}
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-muted-foreground">Loading report…</p>}
      {error && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">Unable to load the report: {error.message}</div>}

      {report && report.missingRolls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CalendarCheck className="h-5 w-5" /> Rolls not taken</CardTitle>
            <CardDescription>These blocks met but have no attendance recorded.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y rounded-md border">
              {report.missingRolls.map((item) => (
                <div key={`${item.scheduleBlock.id}-${item.date}`} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div>
                    <p className="font-medium">{item.scheduleBlock.displayTitle}</p>
                    <p className="text-sm text-muted-foreground">{String(item.date)} · {item.scheduleBlock.timeLabel}</p>
                  </div>
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance/${item.scheduleBlock.id}?date=${item.date}`}>Take attendance</Link>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {report && (
        <Card>
          <CardHeader>
            <CardTitle>Students</CardTitle>
            <CardDescription>Counts across every block a student was marked in. Rate = present + tardy over non-excused records.</CardDescription>
          </CardHeader>
          <CardContent>
            {report.students.length === 0 ? (
              <p className="text-sm text-muted-foreground">No attendance has been recorded in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Student</th>
                      <th className="py-2 pr-4 font-medium">Present</th>
                      <th className="py-2 pr-4 font-medium">Absent</th>
                      <th className="py-2 pr-4 font-medium">Tardy</th>
                      <th className="py-2 pr-4 font-medium">Excused</th>
                      <th className="py-2 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.students.map((student) => (
                      <tr key={student.person.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">{student.person.lastName}, {student.person.firstName}</td>
                        <td className="py-2 pr-4 tabular-nums">{student.presentCount}</td>
                        <td className="py-2 pr-4 tabular-nums">{student.absentCount}</td>
                        <td className="py-2 pr-4 tabular-nums">{student.tardyCount}</td>
                        <td className="py-2 pr-4 tabular-nums">{student.excusedCount}</td>
                        <td className="py-2 tabular-nums">{student.attendanceRate != null ? `${student.attendanceRate}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
